// src/components/analytics/AIMetricsDashboard.js
// --- UPDATE (round 3: real, live AI metrics) ------------------------------------------------------
// Now reads the ML control-plane overview (useMlOverview — refreshes on every 'ml_events' message,
// every 8 s while a model trains, 60 s otherwise) instead of three legacy calls.
// • "Avg latency" showed a made-up 45 ms whenever the engine did not report one — it is the
//   engine's measured per-model latency, or "—".
// • The performance chart multiplied every metric by 100, so the RUL model's MAE of 12 days was
//   drawn as "1200 %". Ratio metrics (F1, accuracy) are charted as %, error metrics (MAE) are
//   listed in their own unit beside the chart.
// • The training table only showed a value when the run reported f1_score — it now shows each
//   run's real outcome (decision, version, error) from the run log.
// • The error banner appeared whenever health.status was not exactly 'ok' (including while
//   loading); it now reflects the engine's real state, and Retry refetches without a reload.
// • Explain buttons on each model; liveVersion from the Analytics Hub also triggers a refresh.
// --- END UPDATE ------------------------------------------------------------------------------------
import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  Heading, Text, Badge, Flex, IconButton, Button, Skeleton,
  Table, Thead, Tbody, Tr, Th, Td, Tooltip,
  useColorModeValue, Icon, Alert, AlertIcon, Code, VStack, HStack,
} from '@chakra-ui/react';
import { MdRefresh, MdCheckCircle, MdError, MdWarning, MdTimeline, MdStorage, MdModelTraining } from 'react-icons/md';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import { useMlOverview } from '../../hooks/useMlOps';
import { ExplainButton } from '../ml/FlowSenseAIExplainer';
import { DECISION_COLOR, DECISION_LABEL, JOB_COLOR, fmtMetric, timeAgo } from '../ml/mlFormat';

const AIMetricsDashboard = ({ liveVersion = 0, onAskAI = null }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const gridStroke = useColorModeValue('#E2E8F0', '#4A5568');

  const { data: ov, loading, error, refresh, lastUpdated } = useMlOverview();

  const lastLive = useRef(liveVersion);
  useEffect(() => {
    if (liveVersion === lastLive.current) return;
    lastLive.current = liveVersion;
    refresh();
  }, [liveVersion, refresh]);

  const models = useMemo(() => ov?.models || [], [ov]);
  const online = ov?.mlEngine?.online ?? null;
  const status = online === null ? 'unknown' : online ? ((ov?.attention || []).some(a => a.level === 'high') ? 'degraded' : 'ok') : 'offline';
  const statusColor = { ok: 'green', degraded: 'orange', offline: 'red', unknown: 'gray' }[status];
  const StatusIcon = { ok: MdCheckCircle, degraded: MdWarning, offline: MdError, unknown: MdWarning }[status];

  // --- UPDATE (round 4): the engine reports avg_duration_ms (avg_latency_ms is the new alias);
  // a missing value must not become 0 ms ---
  const latencies = models
    .map(m => m.engine?.runtime?.avg_latency_ms ?? m.engine?.runtime?.avg_duration_ms)
    .filter(v => v !== null && v !== undefined)
    .map(Number)
    .filter(Number.isFinite);
  // --- END UPDATE ---
  const avgLatency = latencies.length ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length) : null;
  const inProduction = models.filter(m => m.champion).length;
  const totalVersions = ov?.summary?.totalVersions ?? null;

  const ratioData = models
    .filter(m => m.champion && m.metric?.format === 'ratio' && m.champion.metric !== null && m.champion.metric !== undefined)
    .map(m => ({ name: m.name, score: Math.round(Number(m.champion.metric) * 1000) / 10, label: m.metric.label, version: m.champion.model_version }));
  const errorMetrics = models.filter(m => m.champion && m.metric?.format !== 'ratio');

  const jobs = ov?.recentJobs || [];
  const nameOf = (key) => models.find(m => m.key === key)?.name || key;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">AI System Monitor</Heading>
          <Text fontSize="sm" color={muted}>Live model registry, engine health and training runs</Text>
        </VStack>
        <HStack>
          <Text fontSize="xs" color={muted}>{lastUpdated ? `Updated ${timeAgo(lastUpdated)}` : 'Loading…'}</Text>
          {onAskAI && <Button size="sm" variant="outline" colorScheme="purple" onClick={() => onAskAI('How are the FlowSense AI models performing right now, and what should I do about it?')}>Ask AI</Button>}
          <IconButton icon={<MdRefresh />} onClick={refresh} isLoading={loading && !ov} aria-label="Refresh data" />
        </HStack>
      </Flex>

      {error && !ov && (
        <Alert status="error" mb={6} borderRadius="md">
          <AlertIcon />
          <Box flex="1"><Heading size="sm">Could not load AI status</Heading><Text fontSize="sm">{error.message}</Text></Box>
          <Button size="sm" colorScheme="red" variant="outline" onClick={refresh}>Retry</Button>
        </Alert>
      )}
      {ov && online === false && (
        <Alert status="error" mb={6} borderRadius="md" data-testid="engine-offline">
          <AlertIcon />
          <Box flex="1">
            <Heading size="sm">ML engine unreachable</Heading>
            <Text fontSize="sm">Predictions use fallbacks and training is paused.{ov.mlEngine?.error && <Code ml={2} colorScheme="red">{ov.mlEngine.error}</Code>}</Text>
          </Box>
          <Button size="sm" colorScheme="red" variant="outline" onClick={refresh}>Retry</Button>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
        <Stat bg={cardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between">
            <Box>
              <StatLabel>ML engine</StatLabel>
              {ov ? <StatNumber color={`${statusColor}.500`}>{status.toUpperCase()}</StatNumber> : <Skeleton h="28px" w="90px" />}
              <StatHelpText>{ov?.mlEngine?.circuit?.state ? `circuit ${ov.mlEngine.circuit.state}` : 'Python inference service'}</StatHelpText>
            </Box>
            <Icon as={StatusIcon} w={8} h={8} color={`${statusColor}.500`} />
          </Flex>
        </Stat>
        <Stat bg={cardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between">
            <Box>
              <StatLabel>In production</StatLabel>
              {ov ? <StatNumber>{inProduction} / {models.length}</StatNumber> : <Skeleton h="28px" w="60px" />}
              <StatHelpText>{ov?.summary?.activeJobs ? `${ov.summary.activeJobs} training now` : 'models serving predictions'}</StatHelpText>
            </Box>
            <Icon as={MdModelTraining} w={8} h={8} color="blue.500" />
          </Flex>
        </Stat>
        <Stat bg={cardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between">
            <Box>
              <StatLabel>Avg latency</StatLabel>
              {ov ? <StatNumber>{avgLatency !== null ? `${avgLatency} ms` : '—'}</StatNumber> : <Skeleton h="28px" w="60px" />}
              <StatHelpText>{avgLatency !== null ? `measured on ${latencies.length} model${latencies.length === 1 ? '' : 's'}` : 'not reported by the engine'}</StatHelpText>
            </Box>
            <Icon as={MdTimeline} w={8} h={8} color="purple.500" />
          </Flex>
        </Stat>
        <Stat bg={cardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between">
            <Box>
              <StatLabel>Registry</StatLabel>
              {ov ? <StatNumber>{totalVersions ?? '—'}</StatNumber> : <Skeleton h="28px" w="60px" />}
              <StatHelpText>versions in {ov?.registryTable || 'registry'}</StatHelpText>
            </Box>
            <Icon as={MdStorage} w={8} h={8} color="teal.500" />
          </Flex>
        </Stat>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        <Box bg={cardBg} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Heading size="md" mb={1}>Production model performance</Heading>
          <Text fontSize="xs" color={muted} mb={4}>F1 / accuracy of the version serving predictions (higher is better)</Text>
          {!ov ? <Skeleton h="260px" /> : ratioData.length === 0 ? (
            <Text fontSize="sm" color={muted}>No classification model has a production version yet.</Text>
          ) : (
            <Box h={`${Math.max(ratioData.length * 56, 120)}px`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratioData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis type="number" domain={[0, 100]} unit="%" />
                  <YAxis dataKey="name" type="category" width={150} fontSize={12} />
                  <RechartsTooltip formatter={(v, _n, p) => [`${v}% (${p.payload.label}, v${p.payload.version})`, 'Score']} />
                  <Bar dataKey="score" fill="#805AD5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
          {errorMetrics.length > 0 && (
            <VStack align="stretch" spacing={1} mt={4}>
              {errorMetrics.map(m => (
                <HStack key={m.key} justify="space-between" fontSize="sm">
                  <Text>{m.name}</Text>
                  <HStack>
                    <Badge colorScheme="purple">{m.metric.label}: {fmtMetric(m.champion.metric, m.metric)}</Badge>
                    <Text fontSize="xs" color={muted}>lower is better</Text>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          )}
          <HStack mt={4} spacing={1} flexWrap="wrap">
            {models.map(m => <ExplainButton key={m.key} topic={m.key} label={m.name} />)}
          </HStack>
        </Box>

        <Box bg={cardBg} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Heading size="md" mb={4}>Recent training runs</Heading>
          <Box overflowX="auto" maxH="340px">
            <Table size="sm" variant="simple">
              <Thead><Tr><Th>When</Th><Th>Model</Th><Th>Status</Th><Th>Outcome</Th></Tr></Thead>
              <Tbody>
                {!ov && [0, 1, 2].map(i => <Tr key={i}><Td colSpan={4}><Skeleton h="14px" /></Td></Tr>)}
                {jobs.slice(0, 10).map((j) => (
                  <Tr key={j.id}>
                    <Td fontSize="xs" whiteSpace="nowrap">{timeAgo(j.finished_at || j.started_at || j.created_at)}</Td>
                    <Td fontSize="xs" fontWeight="bold">{nameOf(j.model_type)}</Td>
                    <Td><Badge colorScheme={JOB_COLOR[j.status] || 'gray'}>{j.status}</Badge></Td>
                    <Td fontSize="xs">
                      {j.status === 'failed' ? (
                        <Tooltip label={j.error || ''} hasArrow><Text color="red.400" noOfLines={1}>{j.error || 'failed'}</Text></Tooltip>
                      ) : j.decision ? (
                        <Badge colorScheme={DECISION_COLOR[j.decision] || 'gray'} variant="subtle">{DECISION_LABEL[j.decision] || j.decision}{j.model_version ? ` · v${j.model_version}` : ''}</Badge>
                      ) : (j.stage || '—')}
                    </Td>
                  </Tr>
                ))}
                {ov && jobs.length === 0 && <Tr><Td colSpan={4} textAlign="center" color={muted}>No training runs yet.</Td></Tr>}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default AIMetricsDashboard;
